// Professional Onboarding System with AI Matching and Evaluation
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ═══════════════════════════════════════════════════════════════
// API CONFIG
// ═══════════════════════════════════════════════════════════════
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const apiFetch = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
      ...options,
    });
    if (!response.ok) return { error: true, status: response.status };
    return await response.json();
  } catch (err) { return { error: true, message: err.message }; }
};


// Professional Onboarding Context
const ProfessionalOnboardingContext = createContext();

// Action types
const ONBOARDING_ACTIONS = {
  SET_ONBOARDING_STEP: 'SET_ONBOARDING_STEP',
  UPDATE_PROFILE_DATA: 'UPDATE_PROFILE_DATA',
  SET_SKILLS_ASSESSMENT: 'SET_SKILLS_ASSESSMENT',
  SET_PERSONALITY_TEST: 'SET_PERSONALITY_TEST',
  SET_TASK_EVALUATION: 'SET_TASK_EVALUATION',
  SET_AI_MATCHING_RESULTS: 'SET_AI_MATCHING_RESULTS',
  SET_SCORING_RESULTS: 'SET_SCORING_RESULTS',
  SET_PROFILE_COMPLETION: 'SET_PROFILE_COMPLETION',
  SET_VERIFICATION_STATUS: 'SET_VERIFICATION_STATUS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Initial state
const initialState = {
  // Onboarding progress
  currentStep: 1,
  totalSteps: 8,
  completedSteps: [],
  
  // Profile data
  profileData: {
    // Basic Information
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      nationality: '',
      location: {
        country: '',
        city: '',
        address: '',
        postcode: '',
      },
      profilePhoto: null,
      linkedinProfile: '',
      portfolioUrl: '',
    },
    
    // Professional Information
    professionalInfo: {
      currentTitle: '',
      yearsOfExperience: 0,
      industryExperience: [],
      careerLevel: '', // Entry, Mid, Senior, Executive
      expectedSalary: {
        min: 0,
        max: 0,
        currency: 'GBP',
      },
      availability: '', // Immediate, 1 month, 2 months, etc.
      workPreference: '', // Remote, Hybrid, On-site
      visaStatus: '', // UK Citizen, Work Visa, Sponsorship Required
    },
    
    // Education
    education: [],
    
    // Work Experience
    workExperience: [],
    
    // Skills and Competencies
    skills: {
      technical: [],
      soft: [],
      languages: [],
      certifications: [],
    },
    
    // Career Preferences
    careerPreferences: {
      preferredRoles: [],
      preferredIndustries: [],
      preferredCompanySize: [],
      preferredWorkCulture: [],
      careerGoals: '',
      motivations: [],
    },
  },
  
  // AI-powered assessments
  skillsAssessment: {
    technicalTests: [],
    results: {},
    overallScore: 0,
    strengths: [],
    improvementAreas: [],
  },
  
  personalityTest: {
    questions: [],
    answers: {},
    personalityType: '',
    traits: {},
    workStyle: '',
    teamFit: [],
  },
  
  // Task-based evaluation
  taskEvaluation: {
    assignedTasks: [],
    completedTasks: [],
    taskScores: {},
    overallTaskScore: 0,
    evaluatorFeedback: [],
  },
  
  // AI Matching System
  aiMatching: {
    profileAnalysis: {
      nlpScore: 0,
      keywordMatching: {},
      skillsMapping: {},
      experienceMapping: {},
    },
    jobMatches: [],
    matchingScores: {},
    recommendedJobs: [],
    improvementSuggestions: [],
  },
  
  // Scoring and Evaluation
  scoring: {
    profileCompleteness: 0,
    skillsScore: 0,
    experienceScore: 0,
    personalityScore: 0,
    taskScore: 0,
    overallScore: 0,
    ranking: '',
    badges: [],
  },
  
  // Profile status
  profileCompletion: {
    percentage: 0,
    missingFields: [],
    recommendations: [],
  },
  
  verification: {
    emailVerified: false,
    phoneVerified: false,
    identityVerified: false,
    educationVerified: false,
    workExperienceVerified: false,
    skillsVerified: false,
  },
  
  // App state
  isLoading: false,
  error: null,
};

// Reducer
const onboardingReducer = (state, action) => {
  switch (action.type) {
    case ONBOARDING_ACTIONS.SET_ONBOARDING_STEP:
      return {
        ...state,
        currentStep: action.payload.step,
        completedSteps: action.payload.completedSteps || state.completedSteps,
      };
      
    case ONBOARDING_ACTIONS.UPDATE_PROFILE_DATA:
      return {
        ...state,
        profileData: {
          ...state.profileData,
          ...action.payload,
        },
      };
      
    case ONBOARDING_ACTIONS.SET_SKILLS_ASSESSMENT:
      return {
        ...state,
        skillsAssessment: {
          ...state.skillsAssessment,
          ...action.payload,
        },
      };
      
    case ONBOARDING_ACTIONS.SET_PERSONALITY_TEST:
      return {
        ...state,
        personalityTest: {
          ...state.personalityTest,
          ...action.payload,
        },
      };
      
    case ONBOARDING_ACTIONS.SET_TASK_EVALUATION:
      return {
        ...state,
        taskEvaluation: {
          ...state.taskEvaluation,
          ...action.payload,
        },
      };
      
    case ONBOARDING_ACTIONS.SET_AI_MATCHING_RESULTS:
      return {
        ...state,
        aiMatching: {
          ...state.aiMatching,
          ...action.payload,
        },
      };
      
    case ONBOARDING_ACTIONS.SET_SCORING_RESULTS:
      return {
        ...state,
        scoring: {
          ...state.scoring,
          ...action.payload,
        },
      };
      
    case ONBOARDING_ACTIONS.SET_PROFILE_COMPLETION:
      return {
        ...state,
        profileCompletion: {
          ...state.profileCompletion,
          ...action.payload,
        },
      };
      
    case ONBOARDING_ACTIONS.SET_VERIFICATION_STATUS:
      return {
        ...state,
        verification: {
          ...state.verification,
          ...action.payload,
        },
      };
      
    case ONBOARDING_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
      
    case ONBOARDING_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };
      
    case ONBOARDING_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
      
    default:
      return state;
  }
};

// Provider component
export const ProfessionalOnboardingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(onboardingReducer, initialState);

  // Initialize onboarding data
  useEffect(() => {
    loadOnboardingData();
  }, []);

  // Load saved onboarding data
  const loadOnboardingData = async () => {
    try {
      const [
        profileData,
        onboardingProgress,
        skillsAssessment,
        personalityTest,
        taskEvaluation,
        aiMatching,
        scoring,
      ] = await Promise.all([
        AsyncStorage.getItem('onboarding_profile'),
        AsyncStorage.getItem('onboarding_progress'),
        AsyncStorage.getItem('skills_assessment'),
        AsyncStorage.getItem('personality_test'),
        AsyncStorage.getItem('task_evaluation'),
        AsyncStorage.getItem('ai_matching'),
        AsyncStorage.getItem('scoring_results'),
      ]);

      if (profileData) {
        dispatch({
          type: ONBOARDING_ACTIONS.UPDATE_PROFILE_DATA,
          payload: JSON.parse(profileData),
        });
      }

      if (onboardingProgress) {
        const progress = JSON.parse(onboardingProgress);
        dispatch({
          type: ONBOARDING_ACTIONS.SET_ONBOARDING_STEP,
          payload: progress,
        });
      }

      if (skillsAssessment) {
        dispatch({
          type: ONBOARDING_ACTIONS.SET_SKILLS_ASSESSMENT,
          payload: JSON.parse(skillsAssessment),
        });
      }

      if (personalityTest) {
        dispatch({
          type: ONBOARDING_ACTIONS.SET_PERSONALITY_TEST,
          payload: JSON.parse(personalityTest),
        });
      }

      if (taskEvaluation) {
        dispatch({
          type: ONBOARDING_ACTIONS.SET_TASK_EVALUATION,
          payload: JSON.parse(taskEvaluation),
        });
      }

      if (aiMatching) {
        dispatch({
          type: ONBOARDING_ACTIONS.SET_AI_MATCHING_RESULTS,
          payload: JSON.parse(aiMatching),
        });
      }

      if (scoring) {
        dispatch({
          type: ONBOARDING_ACTIONS.SET_SCORING_RESULTS,
          payload: JSON.parse(scoring),
        });
      }

      // Calculate profile completion
      calculateProfileCompletion();
    } catch (error) {
      console.error('Error loading onboarding data:', error);
    }
  };

  // Profile management methods
  const updateProfileData = async (section, data) => {
    try {
      const updatedProfile = {
        ...state.profileData,
        [section]: {
          ...state.profileData[section],
          ...data,
        },
      };

      await AsyncStorage.setItem('onboarding_profile', JSON.stringify(updatedProfile));
      
      dispatch({
        type: ONBOARDING_ACTIONS.UPDATE_PROFILE_DATA,
        payload: { [section]: updatedProfile[section] },
      });

      // Recalculate profile completion
      calculateProfileCompletion();
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile data:', error);
      return { success: false, error: error.message };
    }
  };

  const nextOnboardingStep = async () => {
    try {
      const nextStep = Math.min(state.currentStep + 1, state.totalSteps);
      const completedSteps = [...state.completedSteps, state.currentStep];
      
      const progress = {
        step: nextStep,
        completedSteps,
      };

      await AsyncStorage.setItem('onboarding_progress', JSON.stringify(progress));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_ONBOARDING_STEP,
        payload: progress,
      });
      
      return { success: true, nextStep };
    } catch (error) {
      console.error('Error updating onboarding step:', error);
      return { success: false, error: error.message };
    }
  };

  // Skills assessment methods
  const startSkillsAssessment = async (skillCategory) => {
    try {
      // Generate assessment questions based on skill category
      const questions = generateSkillsQuestions(skillCategory);
      
      const assessment = {
        ...state.skillsAssessment,
        technicalTests: [
          ...state.skillsAssessment.technicalTests,
          {
            category: skillCategory,
            questions,
            startTime: new Date().toISOString(),
            status: 'in_progress',
          },
        ],
      };

      await AsyncStorage.setItem('skills_assessment', JSON.stringify(assessment));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_SKILLS_ASSESSMENT,
        payload: assessment,
      });
      
      return { success: true, questions };
    } catch (error) {
      console.error('Error starting skills assessment:', error);
      return { success: false, error: error.message };
    }
  };

  const submitSkillsAssessment = async (category, answers) => {
    try {
      // Process answers with AI-powered evaluation
      const results = await processSkillsAnswers(category, answers);
      
      const updatedAssessment = {
        ...state.skillsAssessment,
        results: {
          ...state.skillsAssessment.results,
          [category]: results,
        },
        overallScore: calculateOverallSkillsScore(state.skillsAssessment.results, results),
      };

      await AsyncStorage.setItem('skills_assessment', JSON.stringify(updatedAssessment));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_SKILLS_ASSESSMENT,
        payload: updatedAssessment,
      });
      
      return { success: true, results };
    } catch (error) {
      console.error('Error submitting skills assessment:', error);
      return { success: false, error: error.message };
    }
  };

  // Personality test methods
  const startPersonalityTest = async () => {
    try {
      const questions = generatePersonalityQuestions();
      
      const test = {
        ...state.personalityTest,
        questions,
        startTime: new Date().toISOString(),
        status: 'in_progress',
      };

      await AsyncStorage.setItem('personality_test', JSON.stringify(test));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_PERSONALITY_TEST,
        payload: test,
      });
      
      return { success: true, questions };
    } catch (error) {
      console.error('Error starting personality test:', error);
      return { success: false, error: error.message };
    }
  };

  const submitPersonalityTest = async (answers) => {
    try {
      // Analyze personality based on answers
      const analysis = analyzePersonality(answers);
      
      const updatedTest = {
        ...state.personalityTest,
        answers,
        personalityType: analysis.type,
        traits: analysis.traits,
        workStyle: analysis.workStyle,
        teamFit: analysis.teamFit,
        completedAt: new Date().toISOString(),
      };

      await AsyncStorage.setItem('personality_test', JSON.stringify(updatedTest));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_PERSONALITY_TEST,
        payload: updatedTest,
      });
      
      return { success: true, analysis };
    } catch (error) {
      console.error('Error submitting personality test:', error);
      return { success: false, error: error.message };
    }
  };

  // Task evaluation methods
  const assignTask = async (taskType, difficulty = 'medium') => {
    try {
      const task = generateTask(taskType, difficulty);
      
      const updatedEvaluation = {
        ...state.taskEvaluation,
        assignedTasks: [
          ...state.taskEvaluation.assignedTasks,
          {
            ...task,
            assignedAt: new Date().toISOString(),
            status: 'assigned',
          },
        ],
      };

      await AsyncStorage.setItem('task_evaluation', JSON.stringify(updatedEvaluation));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_TASK_EVALUATION,
        payload: updatedEvaluation,
      });
      
      return { success: true, task };
    } catch (error) {
      console.error('Error assigning task:', error);
      return { success: false, error: error.message };
    }
  };

  const submitTaskSolution = async (taskId, solution) => {
    try {
      // Evaluate task solution with AI
      const evaluation = await evaluateTaskSolution(taskId, solution);
      
      const updatedEvaluation = {
        ...state.taskEvaluation,
        completedTasks: [
          ...state.taskEvaluation.completedTasks,
          {
            taskId,
            solution,
            submittedAt: new Date().toISOString(),
            evaluation,
          },
        ],
        taskScores: {
          ...state.taskEvaluation.taskScores,
          [taskId]: evaluation.score,
        },
        overallTaskScore: calculateOverallTaskScore(state.taskEvaluation.taskScores, taskId, evaluation.score),
      };

      await AsyncStorage.setItem('task_evaluation', JSON.stringify(updatedEvaluation));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_TASK_EVALUATION,
        payload: updatedEvaluation,
      });
      
      return { success: true, evaluation };
    } catch (error) {
      console.error('Error submitting task solution:', error);
      return { success: false, error: error.message };
    }
  };

  // AI Matching methods
  const runAIProfileAnalysis = async () => {
    try {
      dispatch({ type: ONBOARDING_ACTIONS.SET_LOADING, payload: true });
      
      // Perform NLP analysis on profile
      const nlpAnalysis = await performNLPAnalysis(state.profileData);
      
      // Generate job matches
      const jobMatches = await generateJobMatches(nlpAnalysis);
      
      // Calculate matching scores
      const matchingScores = calculateMatchingScores(nlpAnalysis, jobMatches);
      
      const aiResults = {
        profileAnalysis: nlpAnalysis,
        jobMatches,
        matchingScores,
        recommendedJobs: jobMatches.slice(0, 10), // Top 10 matches
        improvementSuggestions: generateImprovementSuggestions(nlpAnalysis),
        lastAnalyzed: new Date().toISOString(),
      };

      await AsyncStorage.setItem('ai_matching', JSON.stringify(aiResults));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_AI_MATCHING_RESULTS,
        payload: aiResults,
      });
      
      return { success: true, results: aiResults };
    } catch (error) {
      console.error('Error running AI profile analysis:', error);
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: ONBOARDING_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Scoring methods
  const calculateOverallScore = async () => {
    try {
      const profileScore = calculateProfileScore(state.profileData);
      const skillsScore = state.skillsAssessment.overallScore;
      const personalityScore = calculatePersonalityScore(state.personalityTest);
      const taskScore = state.taskEvaluation.overallTaskScore;
      const experienceScore = calculateExperienceScore(state.profileData.workExperience);
      
      const overallScore = Math.round(
        (profileScore * 0.2) +
        (skillsScore * 0.3) +
        (personalityScore * 0.15) +
        (taskScore * 0.25) +
        (experienceScore * 0.1)
      );
      
      const ranking = determineRanking(overallScore);
      const badges = generateBadges(state);
      
      const scoring = {
        profileCompleteness: profileScore,
        skillsScore,
        experienceScore,
        personalityScore,
        taskScore,
        overallScore,
        ranking,
        badges,
        lastCalculated: new Date().toISOString(),
      };

      await AsyncStorage.setItem('scoring_results', JSON.stringify(scoring));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_SCORING_RESULTS,
        payload: scoring,
      });
      
      return { success: true, scoring };
    } catch (error) {
      console.error('Error calculating overall score:', error);
      return { success: false, error: error.message };
    }
  };

  // Profile completion calculation
  const calculateProfileCompletion = () => {
    const requiredFields = [
      'personalInfo.firstName',
      'personalInfo.lastName',
      'personalInfo.email',
      'personalInfo.phone',
      'professionalInfo.currentTitle',
      'professionalInfo.yearsOfExperience',
      'skills.technical',
      'workExperience',
      'education',
    ];
    
    let completedFields = 0;
    const missingFields = [];
    
    requiredFields.forEach(field => {
      const value = getNestedValue(state.profileData, field);
      if (value && (Array.isArray(value) ? value.length > 0 : value.toString().trim())) {
        completedFields++;
      } else {
        missingFields.push(field);
      }
    });
    
    const percentage = Math.round((completedFields / requiredFields.length) * 100);
    
    dispatch({
      type: ONBOARDING_ACTIONS.SET_PROFILE_COMPLETION,
      payload: {
        percentage,
        missingFields,
        recommendations: generateCompletionRecommendations(missingFields),
      },
    });
  };

  // Verification methods
  const verifyEmail = async (verificationCode) => {
    try {
      // Mock email verification
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_VERIFICATION_STATUS,
        payload: { emailVerified: true },
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error verifying email:', error);
      return { success: false, error: error.message };
    }
  };

  const verifyPhone = async (verificationCode) => {
    try {
      // Mock phone verification
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      dispatch({
        type: ONBOARDING_ACTIONS.SET_VERIFICATION_STATUS,
        payload: { phoneVerified: true },
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error verifying phone:', error);
      return { success: false, error: error.message };
    }
  };

  // Utility methods
  const setLoading = (loading) => {
    dispatch({
      type: ONBOARDING_ACTIONS.SET_LOADING,
      payload: loading,
    });
  };

  const setError = (error) => {
    dispatch({
      type: ONBOARDING_ACTIONS.SET_ERROR,
      payload: error,
    });
  };

  const clearError = () => {
    dispatch({
      type: ONBOARDING_ACTIONS.CLEAR_ERROR,
    });
  };

  // Context value
  // ─── Computed convenience fields ─────────────────────────────
  // Expose job_title, email, skills at top level so screens don't
  // have to drill into profileData.professionalInfo.currentTitle
  const job_title =
    state.profileData?.professionalInfo?.currentTitle ||
    state.profileData?.personalInfo?.jobTitle ||
    '';
  const email =
    state.profileData?.personalInfo?.email || '';
  const allSkills = [
    ...(state.profileData?.skills?.technical || []),
    ...(state.profileData?.skills?.soft || []),
  ];

  // ─── Sync profile from API on mount ───────────────────────────
  React.useEffect(() => {
    const syncFromAPI = async () => {
      try {
        const cachedEmail = await AsyncStorage.getItem('userEmail');
        const checkEmail = email || cachedEmail;
        if (!checkEmail) return;
        const data = await apiFetch(`/api/data/customers?email=${encodeURIComponent(checkEmail)}&limit=1`);
        if (data && !data.error) {
          const record = Array.isArray(data) ? data[0] : (data.data?.[0] || data);
          if (record) {
            updateProfileData({
              personalInfo: {
                firstName: record.first_name || record.full_name?.split(' ')[0] || '',
                lastName: record.last_name || record.full_name?.split(' ').slice(1).join(' ') || '',
                email: record.email || '',
                phone: record.phone || '',
              },
              professionalInfo: {
                currentTitle: record.job_title || record.profession || record.current_title || '',
                yearsOfExperience: record.experience_years || 0,
              },
              skills: {
                technical: Array.isArray(record.skills) ? record.skills : [],
                soft: [],
                languages: [],
                certifications: [],
              },
            });
          }
        }
      } catch (e) {}
    };
    syncFromAPI();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    // State
    ...state,

    // ← Top-level convenience (consumed by FindJobsScreen etc.)
    job_title,
    email,
    skills: allSkills,

    // Profile methods
    updateProfileData,
    nextOnboardingStep,
    calculateProfileCompletion,
    
    // Assessment methods
    startSkillsAssessment,
    submitSkillsAssessment,
    startPersonalityTest,
    submitPersonalityTest,
    
    // Task methods
    assignTask,
    submitTaskSolution,
    
    // AI methods
    runAIProfileAnalysis,
    
    // Scoring methods
    calculateOverallScore,
    
    // Verification methods
    verifyEmail,
    verifyPhone,
    
    // Utility methods
    setLoading,
    setError,
    clearError,
  };

  return (
    <ProfessionalOnboardingContext.Provider value={value}>
      {children}
    </ProfessionalOnboardingContext.Provider>
  );
};

// Hook to use Professional Onboarding context
export const useProfessionalOnboarding = () => {
  const context = useContext(ProfessionalOnboardingContext);
  if (!context) {
    throw new Error('useProfessionalOnboarding must be used within a ProfessionalOnboardingProvider');
  }
  return context;
};

// Helper functions
const generateSkillsQuestions = (category) => {
  const questionBank = {
    javascript: [
      {
        id: 1,
        type: 'multiple_choice',
        question: 'What is the output of console.log(typeof null)?',
        options: ['null', 'undefined', 'object', 'boolean'],
        correct: 'object',
        points: 10,
        difficulty: 'medium',
      },
      {
        id: 2,
        type: 'coding',
        question: 'Write a function that returns the factorial of a number.',
        template: 'function factorial(n) {\n  // Your code here\n}',
        testCases: [
          { input: 5, expected: 120 },
          { input: 0, expected: 1 },
          { input: 3, expected: 6 },
        ],
        points: 25,
        difficulty: 'medium',
      },
    ],
    react: [
      {
        id: 3,
        type: 'multiple_choice',
        question: 'Which hook is used for side effects in React?',
        options: ['useState', 'useEffect', 'useContext', 'useReducer'],
        correct: 'useEffect',
        points: 10,
        difficulty: 'easy',
      },
    ],
  };
  
  return questionBank[category] || [];
};

const generatePersonalityQuestions = () => {
  return [
    {
      id: 1,
      question: 'I prefer working in a team rather than alone.',
      type: 'likert',
      scale: 5,
      dimension: 'teamwork',
    },
    {
      id: 2,
      question: 'I enjoy taking on leadership roles.',
      type: 'likert',
      scale: 5,
      dimension: 'leadership',
    },
    {
      id: 3,
      question: 'I adapt quickly to new situations.',
      type: 'likert',
      scale: 5,
      dimension: 'adaptability',
    },
  ];
};

const generateTask = (taskType, difficulty) => {
  const tasks = {
    coding: {
      easy: {
        id: Date.now(),
        title: 'Build a Simple Calculator',
        description: 'Create a basic calculator that can perform addition, subtraction, multiplication, and division.',
        requirements: [
          'Use HTML, CSS, and JavaScript',
          'Include a user interface',
          'Handle edge cases (division by zero)',
          'Add basic styling',
        ],
        timeLimit: 120, // minutes
        maxPoints: 100,
      },
      medium: {
        id: Date.now(),
        title: 'Todo List Application',
        description: 'Build a todo list application with CRUD operations.',
        requirements: [
          'Add, edit, delete todos',
          'Mark todos as complete',
          'Filter todos (all, active, completed)',
          'Persist data in localStorage',
          'Responsive design',
        ],
        timeLimit: 180,
        maxPoints: 150,
      },
    },
  };
  
  return tasks[taskType]?.[difficulty] || tasks.coding.easy;
};

const processSkillsAnswers = async (category, answers) => {
  // Mock AI-powered answer processing
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  let totalScore = 0;
  let maxScore = 0;
  const feedback = [];
  
  answers.forEach((answer, index) => {
    const question = answer.question;
    maxScore += question.points;
    
    if (question.type === 'multiple_choice') {
      if (answer.selected === question.correct) {
        totalScore += question.points;
        feedback.push({
          questionId: question.id,
          correct: true,
          explanation: 'Correct answer!',
        });
      } else {
        feedback.push({
          questionId: question.id,
          correct: false,
          explanation: `Incorrect. The correct answer is: ${question.correct}`,
        });
      }
    } else if (question.type === 'coding') {
      // Mock code evaluation
      const codeScore = Math.floor(Math.random() * question.points);
      totalScore += codeScore;
      feedback.push({
        questionId: question.id,
        score: codeScore,
        maxScore: question.points,
        feedback: 'Code structure is good, but could be optimized.',
      });
    }
  });
  
  return {
    score: Math.round((totalScore / maxScore) * 100),
    totalScore,
    maxScore,
    feedback,
    strengths: ['Problem solving', 'Code structure'],
    improvementAreas: ['Algorithm optimization', 'Error handling'],
  };
};

const analyzePersonality = (answers) => {
  // Mock personality analysis
  const traits = {
    teamwork: 0,
    leadership: 0,
    adaptability: 0,
    creativity: 0,
    analytical: 0,
  };
  
  answers.forEach(answer => {
    if (answer.dimension && traits.hasOwnProperty(answer.dimension)) {
      traits[answer.dimension] = answer.value;
    }
  });
  
  // Determine personality type based on traits
  let personalityType = 'Balanced Professional';
  if (traits.leadership > 4) personalityType = 'Natural Leader';
  if (traits.teamwork > 4) personalityType = 'Team Player';
  if (traits.analytical > 4) personalityType = 'Analytical Thinker';
  
  return {
    type: personalityType,
    traits,
    workStyle: traits.teamwork > 3 ? 'Collaborative' : 'Independent',
    teamFit: ['Cross-functional teams', 'Agile environments'],
  };
};

const evaluateTaskSolution = async (taskId, solution) => {
  // Mock AI task evaluation
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  const criteria = {
    functionality: Math.floor(Math.random() * 30) + 70, // 70-100
    codeQuality: Math.floor(Math.random() * 30) + 60,   // 60-90
    design: Math.floor(Math.random() * 25) + 65,        // 65-90
    creativity: Math.floor(Math.random() * 20) + 70,    // 70-90
  };
  
  const overallScore = Math.round(
    (criteria.functionality * 0.4) +
    (criteria.codeQuality * 0.3) +
    (criteria.design * 0.2) +
    (criteria.creativity * 0.1)
  );
  
  return {
    score: overallScore,
    criteria,
    feedback: [
      'Good implementation of core functionality',
      'Code structure could be improved',
      'Creative approach to the problem',
      'Consider adding error handling',
    ],
    suggestions: [
      'Add input validation',
      'Implement responsive design',
      'Consider accessibility features',
    ],
  };
};

const performNLPAnalysis = async (profileData) => {
  // Mock NLP analysis
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  return {
    nlpScore: 85,
    keywordMatching: {
      technical: ['JavaScript', 'React', 'Node.js', 'Python'],
      soft: ['Leadership', 'Communication', 'Problem-solving'],
      industry: ['Technology', 'Software Development', 'Web Development'],
    },
    skillsMapping: {
      'JavaScript': 0.9,
      'React': 0.8,
      'Node.js': 0.7,
      'Python': 0.6,
    },
    experienceMapping: {
      'Software Development': 0.9,
      'Web Development': 0.8,
      'Project Management': 0.6,
    },
    sentimentAnalysis: {
      overall: 'positive',
      confidence: 0.85,
      keyPhrases: ['passionate about technology', 'eager to learn', 'team player'],
    },
  };
};

const generateJobMatches = async (nlpAnalysis) => {
  // Mock job matching based on NLP analysis
  return [
    {
      id: 'job-1',
      title: 'Senior React Developer',
      company: 'TechCorp',
      matchScore: 92,
      reasons: ['Strong React skills', 'JavaScript expertise', 'Team leadership experience'],
    },
    {
      id: 'job-2',
      title: 'Full Stack Engineer',
      company: 'StartupXYZ',
      matchScore: 88,
      reasons: ['Full stack experience', 'Node.js skills', 'Startup experience'],
    },
  ];
};

const calculateMatchingScores = (nlpAnalysis, jobMatches) => {
  const scores = {};
  jobMatches.forEach(job => {
    scores[job.id] = job.matchScore;
  });
  return scores;
};

const generateImprovementSuggestions = (nlpAnalysis) => {
  return [
    'Consider adding cloud platform certifications (AWS, Azure)',
    'Expand your portfolio with more diverse projects',
    'Add more quantifiable achievements to your experience',
    'Consider learning emerging technologies like AI/ML',
  ];
};

const calculateProfileScore = (profileData) => {
  // Mock profile scoring
  let score = 0;
  
  if (profileData.personalInfo.firstName) score += 10;
  if (profileData.personalInfo.email) score += 10;
  if (profileData.professionalInfo.currentTitle) score += 15;
  if (profileData.workExperience.length > 0) score += 25;
  if (profileData.education.length > 0) score += 15;
  if (profileData.skills.technical.length > 0) score += 25;
  
  return Math.min(score, 100);
};

const calculatePersonalityScore = (personalityTest) => {
  if (!personalityTest.traits) return 0;
  
  const traitValues = Object.values(personalityTest.traits);
  const average = traitValues.reduce((sum, val) => sum + val, 0) / traitValues.length;
  
  return Math.round(average * 20); // Convert to 0-100 scale
};

const calculateExperienceScore = (workExperience) => {
  if (!workExperience || workExperience.length === 0) return 0;
  
  const totalYears = workExperience.reduce((sum, exp) => sum + (exp.years || 0), 0);
  return Math.min(totalYears * 10, 100);
};

const calculateOverallSkillsScore = (existingResults, newResults) => {
  const allScores = [...Object.values(existingResults), newResults.score];
  return Math.round(allScores.reduce((sum, score) => sum + score, 0) / allScores.length);
};

const calculateOverallTaskScore = (existingScores, taskId, newScore) => {
  const allScores = [...Object.values(existingScores), newScore];
  return Math.round(allScores.reduce((sum, score) => sum + score, 0) / allScores.length);
};

const determineRanking = (overallScore) => {
  if (overallScore >= 90) return 'Exceptional';
  if (overallScore >= 80) return 'Excellent';
  if (overallScore >= 70) return 'Good';
  if (overallScore >= 60) return 'Average';
  return 'Needs Improvement';
};

const generateBadges = (state) => {
  const badges = [];
  
  if (state.profileCompletion.percentage >= 100) {
    badges.push({ name: 'Profile Complete', icon: 'check-circle', color: '#43e97b' });
  }
  
  if (state.skillsAssessment.overallScore >= 80) {
    badges.push({ name: 'Skills Expert', icon: 'star', color: '#ffd700' });
  }
  
  if (state.taskEvaluation.overallTaskScore >= 85) {
    badges.push({ name: 'Problem Solver', icon: 'lightbulb', color: '#ff6b6b' });
  }
  
  if (state.verification.emailVerified && state.verification.phoneVerified) {
    badges.push({ name: 'Verified Professional', icon: 'shield-check', color: '#667eea' });
  }
  
  return badges;
};

const generateCompletionRecommendations = (missingFields) => {
  const recommendations = [];
  
  if (missingFields.includes('personalInfo.profilePhoto')) {
    recommendations.push('Add a professional profile photo to increase visibility');
  }
  
  if (missingFields.includes('workExperience')) {
    recommendations.push('Add your work experience to showcase your background');
  }
  
  if (missingFields.includes('skills.technical')) {
    recommendations.push('List your technical skills to improve job matching');
  }
  
  return recommendations;
};

const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

export default ProfessionalOnboardingContext;
